import type { EndpointParameters } from '@/generated/internal-api/LegacyApiRoutes.ts';
import { errorToast, type ToastBody } from '@/helpers';
import type { PickRequired } from '@/model/Common.model';
import InVivoError from '@/model/InVivoError.ts';
import http from '@/support/http';
import { ExceptionHandler } from '@/utils/ExceptionHandler';
import { type AxiosRequestConfig, type AxiosResponse, type Method } from 'axios';
import { notAborted } from './Hooks/fetch/useAbortController';
import { RouteService } from './RouteService';

export const ResponseType = {
  Success: 'success',
  Error: 'error',
  NotFound: 'not_found',
} as const;
export type ResponseType = (typeof ResponseType)[keyof typeof ResponseType];

export interface LegacyApiServiceProps<ApiRoute extends keyof EndpointParameters> {
  method: Method;
  apiRoute: ApiRoute;
  path?: EndpointParameters[ApiRoute];
  query?: Record<string, string | number | boolean | Array<string | number | boolean>>;
  headers?: Record<string, string | number | boolean | Array<string>>;
  body?: any;
  options?: {
    requestConfigOverrides?: Omit<AxiosRequestConfig, 'baseURL' | 'method' | 'headers' | 'data' | 'signal'>;
    onError?: {
      toast?: boolean;
      toast_message?: ToastBody;
      throw?: boolean;
      capture?: boolean;
      slug?: string;
      preserveOriginalError?: boolean;
    };
  };
}

interface CommonLegacyApiResponse<R = unknown> {
  body?: R;
  error?: Error & { response?: { data: { errors: Record<string, Array<string>> }; status: number } };
}

export interface LegacyApiSuccessResponse<R = unknown>
  extends PickRequired<CommonLegacyApiResponse<R>, 'body'>, Pick<AxiosResponse, 'headers' | 'status' | 'statusText'> {
  type: typeof ResponseType.Success;
}

export interface LegacyApiErrorResponse<R = unknown> extends PickRequired<CommonLegacyApiResponse<R>, 'error'> {
  type: typeof ResponseType.Error;
}

export type LegacyApiResponse<R = unknown> = LegacyApiSuccessResponse<R> | LegacyApiErrorResponse<R>;

export class LegacyApiService {
  static async call<ApiRoute extends keyof EndpointParameters>({
    method,
    apiRoute,
    path,
    query,
    headers: requestHeaders,
    body,
    signal,
    options,
  }: LegacyApiServiceProps<ApiRoute> & {
    signal?: AbortSignal;
  }): Promise<LegacyApiResponse<unknown>> {
    try {
      const currentUrl = RouteService.legacyApi({ apiRoute, path, query }).url.href;

      const { data, headers, status, statusText } = await http.request<Response>({
        baseURL: currentUrl,
        method,
        headers: { ...(requestHeaders ?? {}) },
        data: body,
        signal,
        ...(options?.requestConfigOverrides ?? {}),
      });
      return {
        type: ResponseType.Success,
        body: data,
        headers,
        status,
        statusText,
      } as LegacyApiResponse<Response>;
    } catch (error) {
      const errorResponse: LegacyApiErrorResponse<Response> = {
        type: ResponseType.Error,
        error: error as LegacyApiErrorResponse<Response>['error'],
      };
      if (options?.onError?.capture ?? true) {
        ExceptionHandler.captureException(
          new InVivoError(`Api Error: ${apiRoute}`, {
            cause: error,
            slug: options?.onError?.slug ?? 'legacy-api-service',
          })
        );
      }

      if ((options?.onError?.toast ?? true) && notAborted(error)) {
        errorToast(
          options?.onError?.toast_message ?? 'There was a problem communicating with the Benchling In Vivo server'
        );
      }
      if (options?.onError?.throw ?? true) {
        if (options?.onError?.preserveOriginalError) {
          throw error;
        } else {
          throw new InVivoError(errorResponse.type, { cause: error, slug: options?.onError?.slug ?? 'api-service' });
        }
      } else {
        return errorResponse;
      }
    }
  }
}
